// Instant "taste signals" for the dashboard hero (§5.2) — characterful gauges
// derived purely from the distilled profile, no enrichment needed. These replace
// the flat watched/rated/streak counters with things that actually say something
// about the viewer.

import type { DistilledProfile } from "./types.ts";

export type CriticType = "Generous" | "Balanced" | "Demanding";

export interface TasteSignals {
  /** Mean star rating, 0 if unrated. */
  avgRating: number;
  criticType: CriticType;
  /** One-line characterization of the rating personality. */
  criticBlurb: string;
  /** Decade the viewer watches most (e.g. "2010s"), or null. */
  definingDecade: string | null;
  /** Share of watched films in that decade, 0..1. */
  definingDecadeShare: number;
  /** How many decades of film they've watched across. */
  erasSpanned: number;
  /** Watchlist size — the backlog. */
  backlog: number;
  /** Total watched — kept as context, not a hero number. */
  library: number;
  /** Share of watched films they bothered to rate, 0..1. */
  ratedShare: number;
}

/** Top rated (>=4) films, most-loved first — for the drifting hero filmstrip. */
export function topLoved(p: DistilledProfile, n: number): { uri: string; title: string; year?: number }[] {
  return Object.entries(p.films)
    .filter(([, f]) => typeof f.r === "number" && (f.r as number) >= 4)
    .sort((a, b) => (b[1].r as number) - (a[1].r as number))
    .slice(0, n)
    .map(([uri, f]) => ({ uri, title: f.t, year: f.y }));
}

export function computeTasteSignals(profile: DistilledProfile): TasteSignals {
  const { films, aggregates, counts } = profile;

  // Average rating from the histogram (already aggregated).
  let sum = 0;
  let n = 0;
  for (const [rating, c] of Object.entries(aggregates.ratingHist)) {
    sum += parseFloat(rating) * c;
    n += c;
  }
  const avgRating = n === 0 ? 0 : sum / n;

  const criticType: CriticType =
    n === 0 ? "Balanced" : avgRating >= 3.8 ? "Generous" : avgRating < 3.1 ? "Demanding" : "Balanced";
  const criticBlurb =
    n === 0
      ? "You watch more than you score."
      : criticType === "Generous"
        ? "You find the good in almost everything."
        : criticType === "Demanding"
          ? "Stars are earned, not given."
          : "You call them as you see them.";

  // Defining decade.
  const decades = Object.entries(aggregates.byDecade);
  let definingDecade: string | null = null;
  let definingCount = 0;
  let decadeTotal = 0;
  for (const [d, c] of decades) {
    decadeTotal += c;
    if (c > definingCount) {
      definingCount = c;
      definingDecade = d;
    }
  }
  const definingDecadeShare = decadeTotal === 0 ? 0 : definingCount / decadeTotal;
  const erasSpanned = decades.filter(([, c]) => c > 0).length;

  const backlog = counts.watchlist;
  const library = counts.watched;
  const ratedShare = counts.watched === 0 ? 0 : counts.rated / counts.watched;

  // Guard: `films` unused directly but kept in signature for future signals.
  void films;

  return {
    avgRating,
    criticType,
    criticBlurb,
    definingDecade: definingDecade ? `${definingDecade}s` : null,
    definingDecadeShare,
    erasSpanned,
    backlog,
    library,
    ratedShare,
  };
}
